import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSLoader,
  WSSectionToolbar,
  WSTable,
  WSTableColumn,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";

import { queriesPayeeRows } from "@query";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useVersionQuickReferenceModal } from "../VersionQuickReferenceModal";
import { routeHelpersPayeesAll } from "../../../Payees/utils";
import { PayeeFilterEligibilityStatus } from "../../../Payees/routes/RoutePayees/filters/types";

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  viewingVersion: number;
};

type TableItem = {
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
};

const ContractorsCell: React.FC<{
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
}> = ({ isLatestVersion, requirementDefinitionId, version }) => {
  const goToPayeesAll = routeHelpersPayeesAll.useGoTo();

  const xWingspanAccount = useXWingspanExpansion();
  const queryTotal = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_definitionId: { eq: requirementDefinitionId },
        requirements_definitionVersion: { eq: `${version}` }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  if (queryTotal.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const total = queryTotal.data ?? 0;
  const label = isLatestVersion ? `${total} + new` : String(total);

  return (
    <WSFlexBox.CenterY
      gap="XS"
      justify="space-between"
      fullWidth
      onClick={() => {
        goToPayeesAll({
          search: {
            requirementDefinitionId,
            requirementDefinitionVersion: `${version}`,
            eligibilityStatus: PayeeFilterEligibilityStatus.SpecificRequirement
          }
        });
      }}
    >
      <WSText.ParagraphSm>{label}</WSText.ParagraphSm>

      {total ? <WSIcon name="chevron-right" size="S" color="blue500" /> : null}
    </WSFlexBox.CenterY>
  );
};

const getTableColumns = (
  viewingVersion: number
): WSTableColumn<TableItem>[] => [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Version" }
    },
    renderFunction: ({ data }) => (
      <WSList.Horizontal fullWidth gap="M" justify="space-between">
        <WSText.ParagraphSm>v{data.version}</WSText.ParagraphSm>
        {data.version === viewingVersion ? (
          <WSFlexBox.CenterY gap="XS" justify="flex-end">
            <WSIcon color="blue500" name="view" size="S" />
            <WSText.ParagraphSm color="blue500" weight="book">
              Viewing
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        ) : null}
      </WSList.Horizontal>
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Contractors" }
    },
    renderFunction: ({ data }) => (
      <ContractorsCell
        isLatestVersion={data.isLatestVersion}
        requirementDefinitionId={data.requirementDefinitionId}
        version={data.version}
      />
    )
  }
];

export const VersionsWidget: React.FC<Props> = ({
  requirementDefinition,
  viewingVersion
}) => {
  const goToPayeesAll = routeHelpersPayeesAll.useGoTo();

  const versionQuickReferenceModal = useVersionQuickReferenceModal();
  const { versionHistory } = requirementDefinition;

  if (!versionHistory?.length) {
    return null;
  }

  const latestVersion = requirementDefinition.version;
  const { requirementDefinitionId } = requirementDefinition;

  const tableData: WSTableItem<TableItem>[] = [
    {
      data: {
        isLatestVersion: true,
        requirementDefinitionId,
        version: latestVersion
      },
      id: `${latestVersion}`
    },
    ...versionHistory.map(v => ({
      data: {
        isLatestVersion: false,
        requirementDefinitionId,
        version: v.version
      },
      id: `${v.version}`
    }))
  ].sort((a, b) => b.data.version - a.data.version);

  const columns = getTableColumns(viewingVersion);

  return (
    <WSElement>
      <WSSectionToolbar
        buttons={[
          {
            hidden: true,
            label: "View details",
            onClick: () => {
              versionQuickReferenceModal.open({
                requirementDefinitionId:
                  requirementDefinition.requirementDefinitionId
              });
            }
          }
        ]}
        title="Active versions"
      />
      <WSTable<TableItem>
        columns={columns}
        mt="M"
        showVerticalDivider
        tableData={tableData}
      />
    </WSElement>
  );
};
